import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

// Fetch products with filters (Shop page — shared array)
export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (params = {}, { rejectWithValue, signal }) => {
    try {
      const response = await api.get('/products', { params, signal });
      return response.data.data;
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED' || signal.aborted) {
        throw error;
      }
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch products');
    }
  }
);

// ── Home page section fetches (separate keys, don't overwrite shop list) ──

export const fetchFeaturedProducts = createAsyncThunk(
  'products/fetchFeaturedProducts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/products', { params: { featured: 'true', limit: 8 } });
      return response.data.data.products;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch featured products');
    }
  }
);

export const fetchOnSaleProducts = createAsyncThunk(
  'products/fetchOnSaleProducts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/products', { params: { onSale: 'true', limit: 8 } });
      return response.data.data.products;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch on-sale products');
    }
  }
);

export const fetchNewArrivalProducts = createAsyncThunk(
  'products/fetchNewArrivalProducts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/products', { params: { newArrivals: 'true', limit: 8 } });
      return response.data.data.products;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch new arrivals');
    }
  }
);

export const fetchJewelleryProducts = createAsyncThunk(
  'products/fetchJewelleryProducts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/products', { params: { productType: 'jewellery', limit: 8 } });
      return response.data.data.products;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch jewellery products');
    }
  }
);

export const fetchAccessoriesProducts = createAsyncThunk(
  'products/fetchAccessoriesProducts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/products', { params: { productType: 'accessory', limit: 8 } });
      return response.data.data.products;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch accessories');
    }
  }
);


export const fetchBestsellerProducts = createAsyncThunk(
  'products/fetchBestsellerProducts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/products', { params: { sort: 'popular', limit: 8 } });
      return response.data.data.products;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch bestsellers');
    }
  }
);

// Home: Fetch products grouped by category for category sections
export const fetchCategorySectionProducts = createAsyncThunk(
  'products/fetchCategorySectionProducts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/products/home/category-sections');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch category sections');
    }
  }
);

// Fetch single product
export const fetchProduct = createAsyncThunk(
  'products/fetchProduct',
  async (slug, { rejectWithValue }) => {
    try {
      const response = await api.get(`/products/${slug}`);
      return response.data.data.product;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Product not found');
    }
  }
);

// Admin: Fetch all products
export const fetchAdminProducts = createAsyncThunk(
  'products/fetchAdminProducts',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/products/admin/all', { params });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch products');
    }
  }
);

// Admin: Create product
export const createProduct = createAsyncThunk(
  'products/createProduct',
  async (productData, { rejectWithValue }) => {
    try {
      const config = productData instanceof FormData
        ? { headers: { 'Content-Type': 'multipart/form-data' } }
        : {};
      const response = await api.post('/products', productData, config);
      return response.data.data.product;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create product');
    }
  }
);

// Admin: Update product
export const updateProduct = createAsyncThunk(
  'products/updateProduct',
  async ({ id, productData }, { rejectWithValue }) => {
    try {
      const config = productData instanceof FormData
        ? { headers: { 'Content-Type': 'multipart/form-data' } }
        : {};
      const response = await api.put(`/products/${id}`, productData, config);
      return response.data.data.product;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update product');
    }
  }
);

// Admin: Delete product
export const deleteProduct = createAsyncThunk(
  'products/deleteProduct',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/products/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete product');
    }
  }
);

// Admin: Bulk update product status
export const bulkUpdateProductStatus = createAsyncThunk(
  'products/bulkUpdateProductStatus',
  async ({ productIds, status }, { rejectWithValue }) => {
    try {
      const response = await api.patch('/products/admin/bulk', { productIds, status });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update selected products');
    }
  }
);

const initialState = {
  // Shop page list
  products: [],
  product: null,
  availableColours: [],
  pagination: { current: 1, pages: 1, total: 0 },
  filters: {
    category: '',
    minPrice: '',
    maxPrice: '',
    size: '',
    colour: '',
    sort: 'newest',
  },
  // Home page sections (separate — never overwrite shop list)
  featuredProducts: [],
  onSaleProducts: [],
  newArrivalProducts: [],
  jewelleryProducts: [],
  accessoriesProducts: [],
  bestsellerProducts: [],
  categorySectionProducts: [],
  categorySectionsLoading: false,
  homeSectionsLoading: false,
  isLoading: false,
  error: null,
};

const productSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearProduct: (state) => {
      state.product = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ── Fetch Products (shop page) ──
      .addCase(fetchProducts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.products = action.payload.products;
        state.pagination = action.payload.pagination;
        state.availableColours = action.payload.availableColours || [];
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.isLoading = false;
        state.error = action.payload;
      })

      // ── Featured Products (home) ──
      .addCase(fetchFeaturedProducts.pending, (state) => {
        state.homeSectionsLoading = true;
      })
      .addCase(fetchFeaturedProducts.fulfilled, (state, action) => {
        state.homeSectionsLoading = false;
        state.featuredProducts = action.payload;
      })
      .addCase(fetchFeaturedProducts.rejected, (state) => {
        state.homeSectionsLoading = false;
      })

      // ── On Sale Products (home) ──
      .addCase(fetchOnSaleProducts.pending, (state) => {
        state.homeSectionsLoading = true;
      })
      .addCase(fetchOnSaleProducts.fulfilled, (state, action) => {
        state.homeSectionsLoading = false;
        state.onSaleProducts = action.payload;
      })
      .addCase(fetchOnSaleProducts.rejected, (state) => {
        state.homeSectionsLoading = false;
      })

      // ── New Arrival Products (home) ──
      .addCase(fetchNewArrivalProducts.pending, (state) => {
        state.homeSectionsLoading = true;
      })
      .addCase(fetchNewArrivalProducts.fulfilled, (state, action) => {
        state.homeSectionsLoading = false;
        state.newArrivalProducts = action.payload;
      })
      .addCase(fetchNewArrivalProducts.rejected, (state) => {
        state.homeSectionsLoading = false;
      })

      // ── Jewellery Products (home) ──
      .addCase(fetchJewelleryProducts.pending, (state) => {
        state.homeSectionsLoading = true;
      })
      .addCase(fetchJewelleryProducts.fulfilled, (state, action) => {
        state.homeSectionsLoading = false;
        state.jewelleryProducts = action.payload;
      })
      .addCase(fetchJewelleryProducts.rejected, (state) => {
        state.homeSectionsLoading = false;
      })

      // ── Accessories Products (home) ──
      .addCase(fetchAccessoriesProducts.pending, (state) => {
        state.homeSectionsLoading = true;
      })
      .addCase(fetchAccessoriesProducts.fulfilled, (state, action) => {
        state.homeSectionsLoading = false;
        state.accessoriesProducts = action.payload;
      })
      .addCase(fetchAccessoriesProducts.rejected, (state) => {
        state.homeSectionsLoading = false;
      })

      // ── Bestsellers (home) ──
      .addCase(fetchBestsellerProducts.fulfilled, (state, action) => {
        state.bestsellerProducts = action.payload;
      })

      // ── Category Section Products (home) ──
      .addCase(fetchCategorySectionProducts.pending, (state) => {
        state.categorySectionsLoading = true;
      })
      .addCase(fetchCategorySectionProducts.fulfilled, (state, action) => {
        state.categorySectionsLoading = false;
        state.categorySectionProducts = action.payload;
      })
      .addCase(fetchCategorySectionProducts.rejected, (state) => {
        state.categorySectionsLoading = false;
      })

      // ── Fetch Single Product ──
      .addCase(fetchProduct.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.product = action.payload;
      })
      .addCase(fetchProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ── Admin Products ──
      .addCase(fetchAdminProducts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAdminProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.products = action.payload.products;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchAdminProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ── Create Product ──
      .addCase(createProduct.fulfilled, (state, action) => {
        state.products.unshift(action.payload);
      })

      // ── Update Product ──
      .addCase(updateProduct.fulfilled, (state, action) => {
        const index = state.products.findIndex(p => p._id === action.payload._id);
        if (index !== -1) state.products[index] = action.payload;
        state.product = action.payload;
      })

      // ── Delete Product ──
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.products = state.products.filter(p => p._id !== action.payload);
      })

      // ── Bulk Update Status ──
      .addCase(bulkUpdateProductStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(bulkUpdateProductStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        const { productIds, status } = action.payload;
        state.products = state.products.map(p =>
          productIds.includes(p._id) ? { ...p, status } : p
        );
      })
      .addCase(bulkUpdateProductStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { setFilters, clearFilters, clearProduct } = productSlice.actions;
export default productSlice.reducer;